import React, { useEffect, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { FormFields } from '~/components/FormFields'
import { FarmerCreateOptionsType, FarmersService } from '~/api/services/farmers/index'
import { useParams } from 'react-router-dom'
import notify from '~/components/Notify'
import { Loader, Icon, Form } from 'semantic-ui-react'
import InfitiveScrollDropown from '~/components/InfitiveScrollDropown'
import PhoneFax from './PhoneFax'
import Dates from './Dates'
import { GeneralInfoWrapper } from '../style'
import { useAuth } from '~/routes/components/AuthProvider'
import { MILK_MONEY_ACCOUNT_SLUG } from '~/Constants'

interface GeneralInfoProps {
  farmersList: any[]
  farmersOptions: FarmerCreateOptionsType
  farmerGroupList: any[]
}

const GeneralInfo: React.FC<GeneralInfoProps> = (props) => {
  const { farmersList, farmersOptions, farmerGroupList } = props
  const {
    control,
    setValue,
    clearErrors,
    formState: { dirtyFields },
    getValues
  } = useFormContext()
  const { t } = useTranslation()
  const { id } = useParams()
  const { systemData: { modules } } = useAuth()

  const [loadingSupplierCheckNo, setLoadingSupplierCheckNo] = useState(false)
  const [correctSupplierNo, setCorrectSupplierNo] = useState(true)

  const uniqueValidationMessage = t('Please enter unique supplier number')
  const watchSupplierNo = useWatch({ control, name: 'supplierNo' })
  const watchParentId = useWatch({ control, name: 'parentId' })
  const watchFarmerType = useWatch({ control, name: 'farmerType' })
  const hasMilkPaymentModule = Boolean(modules.find(module => module.slug === MILK_MONEY_ACCOUNT_SLUG))

  useEffect(() => {
    if (correctSupplierNo) {
      clearErrors('supplierNo')
    }
  }, [correctSupplierNo])

  useEffect(() => {
    if (watchParentId) {
      clearErrors('parentId')
    }
  }, [watchParentId])

  useEffect(() => {
    if (watchFarmerType && watchFarmerType === 'main') {
      setValue('parentId', null)
    }
  }, [watchFarmerType])

  async function checkSupplierNo(value) {
    setValue('supplierNo', value)
    setLoadingSupplierCheckNo(true)
    try {
      const payload = id ? { supplierNo: value, id } : { supplierNo: value }
      const response = await FarmersService.checkSupplierNo({ ...payload })
      setCorrectSupplierNo(Boolean(response.data))
    } catch (error) {
      if (error.response.data) {
        notify(error.response.data.message, 'error')
      } else {
        notify(t('General error'), 'error')
      }
    }
    setLoadingSupplierCheckNo(false)
  }

  return <GeneralInfoWrapper>
    <Form.Group widths={'equal'}>
      <FormFields.Input
        control={control}
        name={'supplierNo'}
        placeholder={t('Suplier no.')}
        onChange={(e, data) => checkSupplierNo(data.value)}
        rules={{
          required: true,
          validate: () => !watchSupplierNo || watchSupplierNo === '' ? true : correctSupplierNo || uniqueValidationMessage
        }}
        label={<label style={{ display: 'flex', position: 'relative' }}>
          <div>{t('Suplier no.')}<span style={{ color: 'red' }}>*</span></div>
          {loadingSupplierCheckNo && <div className='loaderWrapper'>
            <Loader size='small' active={loadingSupplierCheckNo} />
          </div>}
          {!loadingSupplierCheckNo && watchSupplierNo !== '' && dirtyFields?.supplierNo && <Icon
            style={{ marginLeft: 10 }}
            name={correctSupplierNo ? 'checkmark' : 'close'}
            color={correctSupplierNo ? 'green' : 'red'}
          />}
        </label>}
      />
      <FormFields.Input
        control={control}
        name={'companyName'}
        rules={{
          required: {
            value: true,
            message: t('Plese enter company name')
          }
        }}
        placeholder={t('Company name')}
        label={t('Company name')}
      />

      <FormFields.Select
        selectOnBlur={false}
        control={control}
        label={t('Milk type')}
        name={'milkTypeId'}
        rules={{
          required: {
            value: Boolean(farmersOptions?.milkTypes),
            message: t('Please select milk type')
          }
        }}
        clearable
        placeholder={t('Milk type')}
        options={farmersOptions?.milkTypes?.map(milkType => (
          {
            value: milkType?.id || milkType?.key,
            text: milkType?.name || milkType?.label
          }
        )) || []}
      />
      {hasMilkPaymentModule && <FormFields.Input
        control={control}
        label={t('LKV/MPR Pin')}
        placeholder={t('Enter LKV/MPR Pin')}
        name={'mprPin'}
      />}
    </Form.Group>
    <Form.Group widths={'equal'}>
      <FormFields.Select
        control={control}
        name={'farmerType'}
        label={t('Farmer type')}
        selectOnBlur={false}
        defaultValue={watchParentId ? 'sub' : id ? 'main' : watchFarmerType}
        rules={{
          required: {
            value: true,
            message: t('Please select farmer type')
          }
        }}
        placeholder={t('Select farmer type')}
        options={farmersOptions?.farmerTypes?.map(farmerType => ({ value: farmerType.key, text: farmerType.label })) || []}
      />
      {(watchFarmerType === 'sub' || !!watchParentId) && <InfitiveScrollDropown
        control={control}
        selectOnBlur={false}
        name={'parentId'}
        label={t('Main farmer')}
        value={getValues('parentId')}
        rules={{
          required: {
            value: true,
            message: t('Please select main farmer')
          }
        }}
        setValue={setValue}
        placeholder={t('Select main farmer')}
        options={farmersList.map(farmer => ({ value: farmer.id, text: farmer.companyName })) || []}
      />}
      <PhoneFax farmersOptions={farmersOptions} />
    </Form.Group>
    <div>
      <Dates farmerGroupList={farmerGroupList} />
    </div>
  </GeneralInfoWrapper>
}
export default GeneralInfo